import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { useToast } from '../../../contexts/ToastContext';
import { obtenerAuditoria, type PaginaAuditoria, type FiltrosAuditoria } from '../../../services/correoService';
import { mensajeError } from './errores';
import { fmtFecha } from './formato';
import styles from './CorreoModule.module.css';

/** RF-07 — Auditoría del servicio con filtros por fecha y aplicación. */
export default function AuditoriaCorreoPage() {
  const { addToast } = useToast();
  const [filtros, setFiltros] = useState<FiltrosAuditoria>({ pagina: 1, tamano: 50 });
  const [pagina, setPagina] = useState<PaginaAuditoria | null>(null);
  const [cargando, setCargando] = useState(true);

  // Anti-carrera de paginación: solo la última página pedida pinta su resultado.
  const reqId = useRef(0);
  const cargar = useCallback(async (p = 1) => {
    const id = ++reqId.current;
    setCargando(true);
    try {
      const r = await obtenerAuditoria({ ...filtros, pagina: p });
      if (id === reqId.current) setPagina(r);
    } catch (err) {
      if (id === reqId.current) addToast('error', mensajeError(err));
    } finally {
      if (id === reqId.current) setCargando(false);
    }
  }, [filtros, addToast]);

  // Carga inicial una sola vez; luego el usuario recarga con el botón «Buscar».
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(1); }, []);

  const totalPaginas = pagina ? Math.max(1, Math.ceil(pagina.total / pagina.tamano)) : 1;

  return (
    <div>
      <div className={styles.toolbar}>
        <div className={styles.campo}>
          <label htmlFor="au-desde">Desde</label>
          <input id="au-desde" type="date" value={filtros.desde ?? ''}
            onChange={(e) => setFiltros((f) => ({ ...f, desde: e.target.value }))} />
        </div>
        <div className={styles.campo}>
          <label htmlFor="au-hasta">Hasta</label>
          <input id="au-hasta" type="date" value={filtros.hasta ?? ''}
            onChange={(e) => setFiltros((f) => ({ ...f, hasta: e.target.value }))} />
        </div>
        <div className={styles.campo}>
          <label htmlFor="au-client">Aplicación (ClientId)</label>
          <input id="au-client" value={filtros.clientId ?? ''}
            onChange={(e) => setFiltros((f) => ({ ...f, clientId: e.target.value }))} />
        </div>
        <Button variant="secondary" size="sm" onClick={() => cargar(1)} disabled={cargando}>
          <Search size={15} /> Buscar
        </Button>
      </div>

      {cargando ? (
        <div className={styles.vacio}><Loader2 className="spinner" size={28} /></div>
      ) : !pagina || pagina.items.length === 0 ? (
        <div className={styles.vacio}>Sin registros de auditoría para el filtro.</div>
      ) : (
        <>
          <div className={styles.tablaScroll}>
            <table className={styles.tabla}>
              <thead>
                <tr><th>Fecha</th><th>Aplicación</th><th>Acción</th><th>Recurso</th><th>Resultado</th></tr>
              </thead>
              <tbody>
                {pagina.items.map((a) => (
                  <tr key={a.auditoriaId}>
                    <td>{fmtFecha(a.fechaUtc)}</td>
                    <td>{a.clientId}</td>
                    <td><span className={styles.chip}>{a.accion}</span></td>
                    <td>{a.recurso ?? '—'}</td>
                    <td>
                      <span className={`${styles.chip} ${a.resultado === 'OK' ? styles.chipOk : styles.chipErr}`}>
                        {a.resultado}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className={styles.acciones} style={{ marginTop: '1rem', justifyContent: 'flex-end' }}>
            <span className={styles.kpiEtiqueta}>{pagina.total} registros · página {pagina.pagina} de {totalPaginas}</span>
            <Button variant="ghost" size="sm" disabled={pagina.pagina <= 1} onClick={() => cargar(pagina.pagina - 1)}>Anterior</Button>
            <Button variant="ghost" size="sm" disabled={pagina.pagina >= totalPaginas} onClick={() => cargar(pagina.pagina + 1)}>Siguiente</Button>
          </div>
        </>
      )}
    </div>
  );
}
